import { createSelector } from "@reduxjs/toolkit";

import {
  designerNodeConfigOrNullSelector,
  designerNodeTypeIdOrNullSelector,
  getDesignerNodeByIdSelector,
  getParentContextByIdSelector,
  nodeIdParamSelector,
} from "../../state/index.ts";
import type { DesignerNode } from "../../types/index.ts";
import { getContentNode, type SlotRefContentIds } from "./getContentNode.ts";

// A slot ref's content node id comes from the node's parentContext, set by the owning layoutable's
// getChildContexts (e.g. an activity frame mapping its content slot to its placeholder child).
const slotRefContentIdsOf = (
  parentContext: { slotRefContentIds?: SlotRefContentIds } | undefined,
): SlotRefContentIds | undefined => parentContext?.slotRefContentIds;

export const getContentNodeSelector = createSelector(
  [getDesignerNodeByIdSelector, getParentContextByIdSelector],
  (getById, getParentContextById) => (nodeOrId: DesignerNode | number) => {
    const node = typeof nodeOrId === "number" ? getById(nodeOrId) : nodeOrId;
    const slotRefContentIds = slotRefContentIdsOf(getParentContextById(node.id));
    return getContentNode(node, { getById, slotRefContentIds });
  },
);

export const contentNodeOrNullSelector = createSelector(
  [
    getDesignerNodeByIdSelector,
    getParentContextByIdSelector,
    designerNodeConfigOrNullSelector,
    designerNodeTypeIdOrNullSelector,
    nodeIdParamSelector,
  ],
  (getById, getParentContextById, config, typeId, id) => {
    if (!(typeId && config)) {
      return null;
    }
    const slotRefContentIds = slotRefContentIdsOf(getParentContextById(id));
    return getContentNode({ id, typeId, config }, { getById, slotRefContentIds });
  },
);
